import React, { useEffect, useState, FC } from 'react';
import styles from './styles/recaptcha.module.css';

interface RecaptchaProps {
  onSuccess: (captchaVerifyParam: string) => void;
  onCancel: () => void;
  isVisible: boolean;
}

declare global {
  interface Window {
    initAliyunCaptcha?: any;
    AliyunCaptchaConfig?: { region: string; prefix: string };
  }
}

const Recaptcha: FC<RecaptchaProps> = ({ onSuccess, onCancel, isVisible }) => {
  const [scriptLoaded, setScriptLoaded] = useState(false);

  // Load Aliyun Captcha script
  useEffect(() => {
    if (!isVisible) return;

    window.AliyunCaptchaConfig = {
      region: 'cn',
      prefix: '4qhrrbo',
    };

    if (!window.initAliyunCaptcha && !scriptLoaded) {
      const script = document.createElement('script');
      script.src = 'https://o.alicdn.com/captcha-frontend/aliyunCaptcha/AliyunCaptcha.js';
      script.async = true;
      script.onload = () => setScriptLoaded(true);
      document.body.appendChild(script);
    } else if (window.initAliyunCaptcha) {
      setScriptLoaded(true);
    }
  }, [isVisible, scriptLoaded]);

  // Initialize or reset captcha every time modal opens
  useEffect(() => {
    if (!isVisible) return;
    if (!scriptLoaded) return;
    if (!window.initAliyunCaptcha) return;

    // Reset container
    const container = document.getElementById('captcha-element');
    if (container) {
      container.innerHTML = '';
       const loadingEl = document.createElement('div');
        loadingEl.id = 'captcha-loading';
        loadingEl.textContent = '加载验证码中...';
        loadingEl.style.textAlign = 'center';
        loadingEl.style.color = '#666';
        loadingEl.style.padding = '10px 0';
        container?.appendChild(loadingEl);
    }

    let messageEl = document.getElementById('captcha-message') as HTMLElement | null;
    if (messageEl) {
      messageEl.textContent = '';
    }

    // Initialize captcha
    window.initAliyunCaptcha({
      SceneId: '8ticlm1g7',   // Replace with your valid SceneId
      mode: 'embed',
      element: '#captcha-element',
      success: (captchaVerifyParam: any) => {
          // 无论是新建还是已存在，都更新文字
        onSuccess(captchaVerifyParam);
      },
      fail: (err: any) => {
        console.error('Captcha failed:', err); 
        const body = document.getElementById('aliyunCaptcha-sliding-body');
        if (body && err.verifyCode) {
          const messages = {
            T001: '验证通过',
            T005: '测试模式验证通过',
            F001: '疑似攻击请求，请稍后再试',
            F002: '参数为空，请检查前端集成',
            F003: '参数格式不正确，请检查集成代码',
            F004: '测试模式下验证不通过',
            F005: '场景ID不合法，请检查后端配置',
            F006: '场景ID错误，请检查前端场景ID',
            F008: '验证请求重复提交',
            F009: '检测到虚拟设备环境，请使用真实设备',
            F010: 'IP访问过于频繁，请稍后再试',
            F011: '设备访问过于频繁，请稍后再试',
            F012: '前后端SceneID不一致',
            F013: 'Captcha参数缺失，请检查集成代码',
            F014: '无初始化记录，请重新加载验证码',
            F015: '验证失败，请重试',
            F016: 'URL验证策略不通过，请检查控制台配置',
            F017: '协议或参数异常，请检查请求',
            F018: 'Captcha参数重复使用，请刷新页面重试',
            F019: '⏱验证超时，请重新进行验证',
            F020: '参数与场景ID或用户不匹配',
          };

          const code = String(err.verifyCode) as keyof typeof messages;
          let messageEl = document.getElementById('captcha-message') as HTMLElement | null;

          if (!messageEl) {
            messageEl = document.createElement('div');
            messageEl.id = 'captcha-message';
            messageEl.style.textAlign = 'center';
            messageEl.style.color = 'red';
            messageEl.style.padding = '30px 0';
            body.appendChild(messageEl);
          }

          // 无论是新建还是已存在，都更新文字
          messageEl.textContent = messages[code] + ' ( #'+code +' )' || '加载验证码中...';
        }
      },
      slideStyle: { width: 300, height: 40 },
    });

    // Detect when captcha UI has rendered
    const checkReady = setInterval(() => {
      const container = document.getElementById('captcha-element');
      const el = container?.querySelector('div');

      if (el) {
        const captchaLoading = document.getElementById('captcha-loading');
        if(captchaLoading) {
          // captchaLoading.remove();
        }
        clearInterval(checkReady);

        // Wait for float window element to appear, then show it
        const checkFloatWindow = setInterval(() => {
          const floatEl = document.getElementById('aliyunCaptcha-window-float');
          if (floatEl) {
            floatEl.classList.remove('window-hidden');
            floatEl.classList.add('window-show');
            floatEl.style.display = 'block';
            clearInterval(checkFloatWindow);

            const floatBackEl = document.getElementById('aliyunCaptcha-captcha-body');
            if(floatBackEl){
              floatBackEl.style.width = 'auto';
            }

            const closeBtn = floatEl.querySelector('#aliyunCaptcha-btn-close');
            if (closeBtn) {
              closeBtn.addEventListener('click', () => {
                onCancel();
              });
            }
          }
        }, 100);

        return () => clearInterval(checkFloatWindow);
        
      }else{
        const loadingEl = document.createElement('div');
        loadingEl.id = 'captcha-loading';
        loadingEl.textContent = '加载验证码中...';
        loadingEl.style.textAlign = 'center';
        loadingEl.style.color = '#666';
        loadingEl.style.padding = '10px 0';
        container?.appendChild(loadingEl);
      }
    }, 200);

  }, [isVisible, scriptLoaded, onSuccess]);

  if (!isVisible) return null;

  return (
    <div className={styles.recaptchaModal}>
      <div className={styles.recaptchaOverlay} onClick={onCancel} />
      <div className={styles.recaptchaContainer}>
        <div className={styles.recaptchaHeader}>
          <h3>🔐 安全验证</h3>
          <button onClick={onCancel} className={styles.recaptchaClose}>×</button>
        </div>
        <div className={styles.recaptchaContent}>
          <div id="captcha-element"></div>
        </div>
      </div>
    </div>
  );
};

export default Recaptcha;
